import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Package,
  History,
  Eye,
  Calendar,
  ArrowRightLeft,
  UserPlus,
  UserMinus,
  AlertTriangle,
  Laptop,
  Monitor,
  Smartphone,
  Server,
  HardDrive,
  Printer,
  Box,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn, formatDate, formatDateTime, getStatusColor, getInitials } from '@/lib/utils';
import api from '@/services/api';

interface Employee {
  id: string;
  employeeId: string;
  firstName: string;
  lastName: string;
  email: string;
  department?: { id: string; name: string };
  branch?: { id: string; name: string };
}

interface Asset {
  id: string;
  name: string;
  assetTag: string;
  serialNumber?: string;
  status: string;
  assignedDate?: string;
  category?: { id: string; name: string };
  brand?: { id: string; name: string };
  model?: string;
}

interface AssetHistoryEntry {
  id: string;
  action: 'ASSIGNED' | 'UNASSIGNED' | 'TRANSFERRED';
  date: string;
  asset: {
    id: string;
    name: string;
    assetTag: string;
  };
  fromEmployee?: { id: string; firstName?: string; lastName?: string; employeeId?: string };
  toEmployee?: { id: string; firstName?: string; lastName?: string; employeeId?: string };
  performedBy?: { id: string; firstName?: string; lastName?: string; email?: string | null };
  notes?: string;
}

interface AssetSummary {
  total: number;
  byCategory: Record<string, number>;
}

type TabId = 'current' | 'history';

const categoryIcons: Record<string, React.ElementType> = {
  Laptop: Laptop,
  Laptops: Laptop,
  Monitor: Monitor,
  Monitors: Monitor,
  Phone: Smartphone,
  Phones: Smartphone,
  Mobile: Smartphone,
  Server: Server,
  Servers: Server,
  Storage: HardDrive,
  Printer: Printer,
  Printers: Printer,
};

function getCategoryIcon(category: string): React.ElementType {
  return categoryIcons[category] || Box;
}

export default function EmployeeAssetsPage() {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState<TabId>('current');

  // Fetch employee details
  const { data: employee, isLoading: employeeLoading } = useQuery<Employee>({
    queryKey: ['employee', id],
    queryFn: async () => {
      const response = await api.get(`/employees/${id}`);
      return response.data.data;
    },
  });

  // Fetch current assets
  const { data: currentAssets, isLoading: assetsLoading } = useQuery<Asset[]>({
    queryKey: ['employee-assets', id],
    queryFn: async () => {
      const response = await api.get(`/employees/${id}/assets`);
      return response.data.data || [];
    },
  });

  // Fetch asset history
  const { data: assetHistory, isLoading: historyLoading } = useQuery<AssetHistoryEntry[]>({
    queryKey: ['employee-assets-history', id],
    queryFn: async () => {
      const response = await api.get(`/employees/${id}/assets/history`);
      return response.data.data || [];
    },
    enabled: activeTab === 'history',
  });

  // Fetch asset summary
  const { data: assetSummary } = useQuery<AssetSummary>({
    queryKey: ['employee-assets-summary', id],
    queryFn: async () => {
      const response = await api.get(`/employees/${id}/assets/summary`);
      return response.data.data || { total: 0, byCategory: {} };
    },
  });

  // Check for duplicate asset types (validation)
  const duplicateCategories = currentAssets
    ? Object.entries(
        currentAssets.reduce((acc, asset) => {
          const category = asset.category?.name || 'Uncategorized';
          acc[category] = (acc[category] || 0) + 1;
          return acc;
        }, {} as Record<string, number>)
      ).filter(([, count]) => count > 1)
    : [];

  const isLoading = employeeLoading || assetsLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full spinner" />
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Employee not found</p>
        <Button variant="link" asChild className="mt-2">
          <Link to="/employees">Back to Employees</Link>
        </Button>
      </div>
    );
  }

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'ASSIGNED':
        return <UserPlus className="w-4 h-4 text-green-600" />;
      case 'UNASSIGNED':
        return <UserMinus className="w-4 h-4 text-red-600" />;
      case 'TRANSFERRED':
        return <ArrowRightLeft className="w-4 h-4 text-blue-600" />;
      default:
        return <History className="w-4 h-4" />;
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'ASSIGNED':
        return 'bg-green-100 text-green-800';
      case 'UNASSIGNED':
        return 'bg-red-100 text-red-800';
      case 'TRANSFERRED':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to={`/employees/${id}`}>
              <ArrowLeft className="w-5 h-5" />
            </Link>
          </Button>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-xl font-bold text-primary">
                {getInitials(employee.firstName, employee.lastName)}
              </span>
            </div>
            <div>
              <h1 className="text-3xl font-bold">{employee.firstName} {employee.lastName}</h1>
              <div className="flex items-center gap-2 mt-1">
                <span className="font-mono text-sm text-muted-foreground">{employee.employeeId}</span>
                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-primary/10 text-primary">
                  <Package className="w-3 h-3 inline mr-1" />
                  {currentAssets?.length || 0} Assets
                </span>
              </div>
            </div>
          </div>
        </div>
        <Button variant="outline" asChild>
          <Link to={`/employees/${id}`}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Employee
          </Link>
        </Button>
      </div>

      {/* Duplicate Asset Warning */}
      {duplicateCategories.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Duplicate Asset Types Detected</p>
                <p className="text-sm text-amber-700 mt-1">
                  This employee has multiple assets of the same type:{' '}
                  {duplicateCategories.map(([category, count]) => (
                    <span key={category} className="font-medium">
                      {category} ({count})
                    </span>
                  )).reduce((prev, curr, i) => (
                    <>{prev}{i > 0 ? ', ' : ''}{curr}</>
                  ), <></>)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-4">
        {/* Summary Sidebar */}
        <div className="lg:col-span-1 space-y-6">
          {/* Total Assets Card */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Asset Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-muted-foreground">Total Assets</span>
                <span className="text-2xl font-bold">{assetSummary?.total || currentAssets?.length || 0}</span>
              </div>
              <div className="space-y-3">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">By Category</p>
                {assetSummary?.byCategory && Object.keys(assetSummary.byCategory).length > 0 ? (
                  Object.entries(assetSummary.byCategory).map(([category, count]) => {
                    const Icon = getCategoryIcon(category);
                    return (
                      <div key={category} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm">{category}</span>
                        </div>
                        <span className="font-medium">{count}</span>
                      </div>
                    );
                  })
                ) : currentAssets && currentAssets.length > 0 ? (
                  Object.entries(
                    currentAssets.reduce((acc, asset) => {
                      const category = asset.category?.name || 'Uncategorized';
                      acc[category] = (acc[category] || 0) + 1;
                      return acc;
                    }, {} as Record<string, number>)
                  ).map(([category, count]) => {
                    const Icon = getCategoryIcon(category);
                    return (
                      <div key={category} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm">{category}</span>
                        </div>
                        <span className="font-medium">{count}</span>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-sm text-muted-foreground">No assets assigned</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Employee Info */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Employee Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {employee.department && (
                <div>
                  <p className="text-xs text-muted-foreground">Department</p>
                  <p className="text-sm font-medium">{employee.department.name}</p>
                </div>
              )}
              {employee.branch && (
                <div>
                  <p className="text-xs text-muted-foreground">Branch</p>
                  <p className="text-sm font-medium">{employee.branch.name}</p>
                </div>
              )}
              <div>
                <p className="text-xs text-muted-foreground">Email</p>
                <p className="text-sm font-medium truncate">{employee.email}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3 space-y-6">
          {/* Tabs */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Assets
                </CardTitle>
                <div className="flex gap-1">
                  <Button
                    variant={activeTab === 'current' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setActiveTab('current')}
                  >
                    Current Assets
                  </Button>
                  <Button
                    variant={activeTab === 'history' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setActiveTab('history')}
                  >
                    <History className="w-4 h-4 mr-1" />
                    History
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Current Assets Tab */}
              {activeTab === 'current' && (
                <>
                  {!currentAssets || currentAssets.length === 0 ? (
                    <div className="text-center py-12">
                      <Package className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">No assets currently assigned</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="text-left p-3 font-medium">Asset Tag</th>
                            <th className="text-left p-3 font-medium">Name</th>
                            <th className="text-left p-3 font-medium">Category</th>
                            <th className="text-left p-3 font-medium">Brand/Model</th>
                            <th className="text-left p-3 font-medium">Serial Number</th>
                            <th className="text-left p-3 font-medium">Assigned Date</th>
                            <th className="text-left p-3 font-medium">Status</th>
                            <th className="text-right p-3 font-medium">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {currentAssets.map((asset) => (
                            <tr key={asset.id} className="hover:bg-muted/50">
                              <td className="p-3">
                                <span className="font-mono text-sm font-medium text-primary">
                                  {asset.assetTag}
                                </span>
                              </td>
                              <td className="p-3">
                                <p className="font-medium">{asset.name}</p>
                              </td>
                              <td className="p-3">
                                {asset.category?.name || '-'}
                              </td>
                              <td className="p-3">
                                <div>
                                  {asset.brand?.name && (
                                    <p className="text-sm">{asset.brand.name}</p>
                                  )}
                                  {asset.model && (
                                    <p className="text-xs text-muted-foreground">{asset.model}</p>
                                  )}
                                  {!asset.brand?.name && !asset.model && '-'}
                                </div>
                              </td>
                              <td className="p-3">
                                <span className="font-mono text-sm">
                                  {asset.serialNumber || '-'}
                                </span>
                              </td>
                              <td className="p-3">
                                <div className="flex items-center gap-1 text-sm">
                                  <Calendar className="w-3 h-3 text-muted-foreground" />
                                  {asset.assignedDate ? formatDate(asset.assignedDate) : '-'}
                                </div>
                              </td>
                              <td className="p-3">
                                <span className={cn("px-2 py-1 text-xs font-medium rounded-full", getStatusColor(asset.status))}>
                                  {asset.status.replace(/_/g, ' ')}
                                </span>
                              </td>
                              <td className="p-3 text-right">
                                <Button variant="ghost" size="sm" asChild>
                                  <Link to={`/assets/${asset.id}`}>
                                    <Eye className="w-4 h-4 mr-1" />
                                    View
                                  </Link>
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}

              {/* History Tab */}
              {activeTab === 'history' && (
                <>
                  {historyLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="w-6 h-6 border-4 border-primary border-t-transparent rounded-full spinner" />
                    </div>
                  ) : !assetHistory || assetHistory.length === 0 ? (
                    <div className="text-center py-12">
                      <History className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">No asset history available</p>
                    </div>
                  ) : (
                    <div className="space-y-0">
                      {/* Timeline */}
                      <div className="relative">
                        {assetHistory.map((entry, index) => (
                          <div key={entry.id} className="relative flex gap-4 pb-6">
                            {/* Timeline line */}
                            {index < assetHistory.length - 1 && (
                              <div className="absolute left-4 top-8 bottom-0 w-0.5 bg-border" />
                            )}
                            {/* Timeline dot */}
                            <div className={cn(
                              "w-8 h-8 rounded-full flex items-center justify-center shrink-0 z-10",
                              entry.action === 'ASSIGNED' ? 'bg-green-100' :
                              entry.action === 'UNASSIGNED' ? 'bg-red-100' :
                              'bg-blue-100'
                            )}>
                              {getActionIcon(entry.action)}
                            </div>
                            {/* Content */}
                            <div className="flex-1 bg-muted/30 rounded-lg p-4">
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className={cn(
                                      "px-2 py-0.5 text-xs font-medium rounded-full",
                                      getActionColor(entry.action)
                                    )}>
                                      {entry.action}
                                    </span>
                                    <span className="text-sm text-muted-foreground">
                                      {formatDateTime(entry.date)}
                                    </span>
                                  </div>
                                  <p className="font-medium">
                                    <Link to={`/assets/${entry.asset.id}`} className="hover:underline text-primary">
                                      {entry.asset.assetTag}
                                    </Link>
                                    {' - '}{entry.asset.name}
                                  </p>
                                  {/* Assignment details */}
                                  {entry.action === 'ASSIGNED' && entry.toEmployee && (
                                    <p className="text-sm text-muted-foreground mt-1">
                                      Assigned to: {entry.toEmployee.firstName} {entry.toEmployee.lastName}
                                      {entry.toEmployee.employeeId && ` (${entry.toEmployee.employeeId})`}
                                    </p>
                                  )}
                                  {entry.action === 'UNASSIGNED' && entry.fromEmployee && (
                                    <p className="text-sm text-muted-foreground mt-1">
                                      Unassigned from: {entry.fromEmployee.firstName} {entry.fromEmployee.lastName}
                                      {entry.fromEmployee.employeeId && ` (${entry.fromEmployee.employeeId})`}
                                    </p>
                                  )}
                                  {/* Transfer details */}
                                  {entry.action === 'TRANSFERRED' && (
                                    <p className="text-sm text-muted-foreground mt-1">
                                      From: {entry.fromEmployee ? `${entry.fromEmployee.firstName} ${entry.fromEmployee.lastName}` : 'Unassigned'}
                                      {' → '}
                                      To: {entry.toEmployee ? `${entry.toEmployee.firstName} ${entry.toEmployee.lastName}` : 'Unassigned'}
                                    </p>
                                  )}
                                  {entry.notes && (
                                    <p className="text-sm text-muted-foreground mt-1 italic">{entry.notes}</p>
                                  )}
                                </div>
                                {entry.performedBy && (
                                  <div className="text-right">
                                    <p className="text-xs text-muted-foreground">
                                      by {entry.performedBy.firstName} {entry.performedBy.lastName}
                                    </p>
                                    {entry.performedBy.email && (
                                      <p className="text-xs text-muted-foreground">
                                        {entry.performedBy.email}
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
